import { FC } from 'react';
import { capitalize } from 'lodash/string';
import { i18n as I18n } from 'utils/i18n';
import InputError from '../fields/inputError';

import useCounter from '../../hooks/useCounter/index';

interface Props {
  label: string;
  id: string;
  initialValue: number;
  minValue: number;
  maxValue?: number;
  onChange?: (value: number) => void;
}

const IncrementalInput: FC<Props> = ({
  label,
  id,
  initialValue = 0,
  minValue = 0,
  maxValue,
  onChange = () => {},
}) => {
  const { value, increment, decrement, setTo } = useCounter(initialValue);

  const handleValueChange = event => {
    const { value: strValue } = event.currentTarget;
    const newValue = parseInt(strValue, 10);

    if (Number.isInteger(newValue)) {
      setTo(newValue);
      onChange(newValue);
    } else {
      setTo(null);
      onChange(0);
    }
  };

  const maxLength = () =>
    Math.max(minValue.toString().length, maxValue.toString().length);

  const errorMessage = maxValue
    ? I18n.t('errors.messages.between', { min: minValue, max: maxValue })
    : I18n.t('errors.messages.greater_than', { count: minValue });

  return (
    <>
      <div className="filter__increment">
        <label htmlFor={id}>{label}</label>
        <button
          data-testid="minusBtn"
          onClick={() => {
            decrement();
            onChange(value - 1);
          }}
          disabled={value <= minValue}
        >
          -
        </button>
        <input
          data-testid="incrementInput"
          id={id}
          value={value === null ? '' : value}
          onChange={handleValueChange}
          {...(maxValue ? { maxLength: maxLength() } : {})}
        />
        <button
          data-testid="plusBtn"
          data-cy="plusBtn"
          onClick={() => {
            increment();
            onChange(value + 1);
          }}
          disabled={maxValue && value >= maxValue}
        >
          +
        </button>
      </div>

      {value !== 0 && (value < minValue || (maxValue && value > maxValue)) && (
        <InputError errorMsg={capitalize(errorMessage)} />
      )}
    </>
  );
};

export default IncrementalInput;
